import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { saveKpis } from '../services/docSenseService';
import { usePermission } from '../../../hooks/usePermission';

// ─── Tooltip (portal-based, fast) ────────────────────────────────────────────
function Tooltip({ text, children }) {
  const [pos, setPos] = useState(null);
  const containerRef = useRef(null);
  const handleMouseMove = (e) => setPos({ x: e.clientX, y: e.clientY });
  const handleMouseLeave = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget)) setPos(null);
  };
  return (
    <>
      <span ref={containerRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} style={{ display: 'inline-flex', minWidth: 0, overflow: 'hidden' }}>
        {children}
      </span>
      {pos && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: pos.y + 14, left: pos.x + 8, zIndex: 99999, background: '#1e293b', color: '#f8fafc', fontSize: 11, fontWeight: 500, padding: '6px 10px', borderRadius: 7, boxShadow: '0 4px 16px rgba(0,0,0,0.18)', maxWidth: 300, lineHeight: 1.5, pointerEvents: 'none', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {text}
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildUnitMap = (kpi) => {
  const map = {};
  (kpi.supportedUnits || []).forEach(u => { map[u.unitId] = u.unit; });
  return map;
};

const getUniqueDbs = (kpi) =>
  [...new Set((kpi.emissionFactors || []).map(ef => ef.ghgDatabaseName).filter(Boolean))];

const findEfEntry = (kpi, selectedDb, selectedUnitId) => {
  if (!selectedDb) return null;
  const factors = (kpi.emissionFactors || []).filter(ef => ef.ghgDatabaseName === selectedDb);
  if (!factors.length) return null;
  return factors.find(ef => ef.unitId === selectedUnitId) || factors[0];
};

const calculateTotalEmissions = (kpi, efEntry, selectedUnitId, overrideValue) => {
  if (!efEntry) return null;
  const val = overrideValue != null ? overrideValue : (kpi.value ?? kpi.rawValue);
  if (val == null) return null;
  const value = parseFloat(val);
  if (isNaN(value)) return null;
  const ef = parseFloat(efEntry.emissionFactor);
  if (isNaN(ef)) return null;
  const unitMap = buildUnitMap(kpi);
  const matchedSupported =
    kpi.supportedUnits?.find(u => u.unitId === selectedUnitId) ||
    kpi.supportedUnits?.find(u => unitMap[u.unitId] === (kpi.unit ?? kpi.rawUnit)) ||
    kpi.supportedUnits?.find(u => u.isBase) ||
    kpi.supportedUnits?.[0];
  const matchedFactor = matchedSupported?.factor ?? 1;
  const efUnitFactor = kpi.supportedUnits?.find(u => u.unitId === efEntry.unitId)?.factor ?? 1;
  return (value * matchedFactor / efUnitFactor) * ef;
};

const fmtDate = (d) => {
  try { return d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''; }
  catch { return d || ''; }
};

const toDateInput = (d) => {
  if (!d) return '';
  try { return new Date(d).toISOString().split('T')[0]; }
  catch { return ''; }
};

/** Format scope: "Purchased Goods and Services (Scope 3)" */
const fmtScope = (kpi) => {
  const cat  = kpi.scopeCategory || '';
  const scope = kpi.scope || '';
  if (!cat && !scope) return null;
  if (cat && scope) return `${cat} (${scope})`;
  return cat || scope;
};
const fmtVal = (v) => {
  if (v == null) return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString('en-IN', { maximumFractionDigits: 4 });
};

/** Get the display unit for a kpi from supportedUnits by unitId, or fall back to rawUnit */
const kpiUnit = (kpi, unitId) => {
  const map = buildUnitMap(kpi);
  return map[unitId] || kpi.unit || kpi.rawUnit || '';
};

// ─── Column widths ────────────────────────────────────────────────────────────
// "check" col for Not Saved tab (exclude toggle), "actions" col for Saved tab (delete)
const COL_NORMAL  = { rawTitle:130, kpiTitle:150, rawValue:95, value:80, unit:90, period:168, scope:95, ghgDb:155, ef:170, total:130 };
const COL_UNSAVED = { check:36, rawTitle:124, kpiTitle:150, rawValue:95, value:80, unit:90, period:168, scope:95, ghgDb:155, ef:170, total:130 };
const COL_SAVED   = { rawTitle:130, kpiTitle:150, rawValue:95, value:100, unit:90, period:168, scope:95, ghgDb:155, ef:170, total:130, actions:60 };

const gridCols = (cols) => Object.values(cols).map(w => `${w}px`).join(' ');
const totalW   = (cols) => Object.values(cols).reduce((a, b) => a + b, 0);

// ─── Root ─────────────────────────────────────────────────────────────────────

const KpiReviewTable = ({
  documentId,
  extractedKpis,
  extractedLoading,
  savedKpis,
  savedLoading,
  onSaved,
  onCancelReview,
  refetchExtracted,
  // Inspection-only mode (Evidence Library's per-document DocSense view): only
  // the All/Missing/Matched tabs are shown, and nothing is editable — Not Saved
  // and Saved KPIs both involve saving, which doesn't belong in a read-only view.
  readOnly = false,
}) => {
  const [canSave] = usePermission("doc-sense.save-kpis", "LIST_DOCUMENTS");
  const [canDelete] = usePermission("doc-sense.delete-kpi", "LIST_DOCUMENTS");

  const [activeTab, setActiveTab] = useState('all');
  const isSavedTab   = activeTab === 'saved';
  const isUnsavedTab = activeTab === 'unsaved';
  const [saving, setSaving] = useState(false);

  // ── Extracted tabs row state: kpiId → { unitId, db, periodStart, periodEnd }
  const [rowState, setRowState] = useState({});

  useEffect(() => {
    if (!extractedKpis.length) return;
    setRowState(prev => {
      const next = { ...prev };
      extractedKpis.forEach(kpi => {
        if (next[kpi.id]) return;
        // field rename: unitId replaces unit; fromDate/toDate replace periodStartDate/periodEndDate
        const matchedUnit = kpi.supportedUnits?.find(u => u.unitId === kpi.unitId) || kpi.supportedUnits?.[0];
        const dbs = getUniqueDbs(kpi);
        next[kpi.id] = {
          unitId: matchedUnit?.unitId ?? null,
          db: dbs[0] ?? '',
          periodStart: toDateInput(kpi.fromDate),
          periodEnd: toDateInput(kpi.toDate),
        };
      });
      return next;
    });
  }, [extractedKpis]);

  const updateRow = useCallback((kpiId, patch) => {
    setRowState(prev => ({ ...prev, [kpiId]: { ...prev[kpiId], ...patch } }));
  }, []);

  // ── Unsaved tab: excluded set + select-all helpers
  const [excludedIds, setExcludedIds] = useState(new Set());
  const toggleExclude = useCallback((kpiId) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      next.has(kpiId) ? next.delete(kpiId) : next.add(kpiId);
      return next;
    });
  }, []);
  // selectAll / deselectAll operate on the currently visible (filtered) list
  const selectAllVisible = useCallback((visibleKpis) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      visibleKpis.forEach(k => next.delete(k.id)); // remove from excluded = include
      return next;
    });
  }, []);
  const deselectAllVisible = useCallback((visibleKpis) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      visibleKpis.forEach(k => next.add(k.id)); // add to excluded = deselect
      return next;
    });
  }, []);

  // ── Saved tab state ───────────────────────────────────────────────────────
  const matchedKpis = useMemo(() => extractedKpis.filter(k => !!k.kpiId), [extractedKpis]);
  const hasSavedKpis = !savedLoading && savedKpis.length > 0;

  const [savedTabRows, setSavedTabRows] = useState([]);
  const [savedTabRowState, setSavedTabRowState] = useState({});

  const buildSavedTabRows = useCallback((kpis) => kpis.map(kpi => ({
    ...kpi,
    _savedId: kpi.id,                           // DocSenseKpiEntity.id
    _extractedKpiId: kpi.extractedKpiId ?? null,
    _answerId: kpi.answerId ?? kpi.answer?.id ?? null, // answerId from API saved KPI
    _editedValue: null,
    _deleted: false,
  })), []);

  const buildSavedTabState = useCallback((kpis) => {
    const next = {};
    kpis.forEach(kpi => {
      const matchedUnit = kpi.supportedUnits?.find(u => u.unitId === kpi.unitId) || kpi.supportedUnits?.[0];
      const dbs = getUniqueDbs(kpi);
      next[kpi.id] = {
        unitId: matchedUnit?.unitId ?? null,
        db: dbs[0] ?? '',
        periodStart: toDateInput(kpi.fromDate ?? kpi.periodStartDate),
        periodEnd: toDateInput(kpi.toDate ?? kpi.periodEndDate),
      };
    });
    return next;
  }, []);

  // Re-init saved tab whenever savedKpis reference changes (new fetch from API).
  useEffect(() => {
    if (savedKpis.length > 0) {
      setSavedTabRows(buildSavedTabRows(savedKpis));
      setSavedTabRowState(buildSavedTabState(savedKpis));
    } else {
      setSavedTabRows([]);
      setSavedTabRowState({});
    }
  }, [
    savedKpis,
    savedLoading,
    buildSavedTabRows,
    buildSavedTabState
  ]);

  const updateSavedTabRow = useCallback((kpiId, patch) => {
    setSavedTabRowState(prev => ({ ...prev, [kpiId]: { ...prev[kpiId], ...patch } }));
  }, []);
  const updateSavedRowValue = useCallback((kpiId, value) => {
    setSavedTabRows(prev => prev.map(r => r.id === kpiId ? { ...r, _editedValue: value } : r));
  }, []);
  const deleteSavedRow = useCallback((kpiId) => {
    setSavedTabRows(prev => prev.map(r => r.id === kpiId ? { ...r, _deleted: true } : r));
  }, []);
  const restoreSavedRow = useCallback((kpiId) => {
    setSavedTabRows(prev => prev.map(r => r.id === kpiId ? { ...r, _deleted: false } : r));
  }, []);

  const resetSavedTab = useCallback(() => {
    if (!hasSavedKpis) return;
    setSavedTabRows(buildSavedTabRows(savedKpis));
    setSavedTabRowState(buildSavedTabState(savedKpis));
  }, [savedKpis, hasSavedKpis, buildSavedTabRows, buildSavedTabState]);

  const currentRowState = isSavedTab ? savedTabRowState : rowState;
  const currentUpdateRow = isSavedTab ? updateSavedTabRow : updateRow;

  // ── Tracking: which extractedKpi ids are already saved ───────────────────
  const savedExtractedIds = useMemo(
    () => new Set(savedKpis.map(k => k.extractedKpiId).filter(Boolean)),
    [savedKpis]
  );
  // Not Saved = matched but extractedKpiId not in savedKpis
  const unsavedMatchedKpis = useMemo(
    () => matchedKpis.filter(k => !savedExtractedIds.has(k.id)),
    [matchedKpis, savedExtractedIds]
  );

  // ── Filter state ──────────────────────────────────────────────────────────
  const [filterText, setFilterText] = useState('');
  const [filterScope, setFilterScope] = useState('');
  const [filterDb, setFilterDb] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  // Special filter for Not Saved: "no date set"
  const [filterNoPeriod, setFilterNoPeriod] = useState(false);

  const allDbs = useMemo(() => {
    const set = new Set();
    extractedKpis.forEach(k => getUniqueDbs(k).forEach(db => set.add(db)));
    return [...set];
  }, [extractedKpis]);

  const allScopes = useMemo(() => {
    const set = new Set();
    extractedKpis.forEach(k => { const s = fmtScope(k); if (s) set.add(s); });
    return [...set];
  }, [extractedKpis]);

  // ── Tabs ──────────────────────────────────────────────────────────────────
  const missingKpis = useMemo(() => extractedKpis.filter(k => !k.kpiId), [extractedKpis]);
  const activeSavedRows  = useMemo(() => savedTabRows.filter(r => !r._deleted), [savedTabRows]);
  const deletedSavedRows = useMemo(() => savedTabRows.filter(r => r._deleted), [savedTabRows]);

  const TABS = [
    { key: 'all',     label: 'All KPIs',   count: extractedKpis.length },
    { key: 'missing', label: 'Missing',    count: missingKpis.length },
    { key: 'matched', label: 'Matched',    count: matchedKpis.length },
    ...(readOnly ? [] : [
      { key: 'unsaved', label: 'Not Saved',  count: unsavedMatchedKpis.length, accent: unsavedMatchedKpis.length > 0 },
      { key: 'saved',   label: 'Saved KPIs', count: savedKpis.length },
    ]),
  ];

  const baseExtractedKpis =
    activeTab === 'all'     ? extractedKpis :
    activeTab === 'missing' ? missingKpis :
    activeTab === 'matched' ? matchedKpis :
    activeTab === 'unsaved' ? unsavedMatchedKpis : [];

  const applyFilters = useCallback((kpis, rs) => {
    return kpis.filter(kpi => {
      if (filterText) {
        const q = filterText.toLowerCase();
        if (!(kpi.kpiTitle || '').toLowerCase().includes(q) &&
            !(kpi.rawTitle || '').toLowerCase().includes(q)) return false;
      }
      if (filterScope && fmtScope(kpi) !== filterScope) return false;
      if (filterDb && !getUniqueDbs(kpi).includes(filterDb)) return false;

      const r = rs[kpi.id] || {};
      const rowStart = r.periodStart || toDateInput(kpi.fromDate ?? kpi.periodStartDate);
      const rowEnd   = r.periodEnd   || toDateInput(kpi.toDate ?? kpi.periodEndDate);

      // "No period set" filter
      if (filterNoPeriod) {
        if (rowStart || rowEnd) return false;
      }

      if (filterDateStart || filterDateEnd) {
        if (!rowStart && !rowEnd) return false;
        if (filterDateStart && rowEnd   && rowEnd   < filterDateStart) return false;
        if (filterDateEnd   && rowStart && rowStart > filterDateEnd)   return false;
      }
      return true;
    });
  }, [filterText, filterScope, filterDb, filterDateStart, filterDateEnd, filterNoPeriod]);

  const filteredExtractedKpis = useMemo(
    () => applyFilters(baseExtractedKpis, rowState),
    [baseExtractedKpis, rowState, applyFilters]
  );
  const filteredSavedRows = useMemo(
    () => applyFilters(activeSavedRows, savedTabRowState),
    [activeSavedRows, savedTabRowState, applyFilters]
  );
  // Deleted rows also respect current filters so scope/text filter hides them too
  const filteredDeletedRows = useMemo(
    () => applyFilters(deletedSavedRows, savedTabRowState),
    [deletedSavedRows, savedTabRowState, applyFilters]
  );

  // Bulk delete/restore — placed here so they can reference filteredSavedRows
  const deleteAllVisible = useCallback(() => {
    const ids = new Set(filteredSavedRows.map(r => r.id));
    setSavedTabRows(prev => prev.map(r => ids.has(r.id) ? { ...r, _deleted: true } : r));
  }, [filteredSavedRows]);

  const restoreAllDeleted = useCallback(() => {
    setSavedTabRows(prev => prev.map(r => ({ ...r, _deleted: false })));
  }, []);

  const isLoadingTab  = isSavedTab ? savedLoading : extractedLoading;
  const baseCount     = isSavedTab ? activeSavedRows.length : baseExtractedKpis.length;
  const filteredList  = isSavedTab ? filteredSavedRows : filteredExtractedKpis;

  // ── Total emissions (visible rows only) ───────────────────────────────────
  const totalEmissions = useMemo(() => {
    let sum = 0, hasAny = false;
    filteredList.forEach(kpi => {
      const rs = currentRowState[kpi.id] || {};
      const efEntry = findEfEntry(kpi, rs.db ?? '', rs.unitId ?? null);
      const override = isSavedTab ? (kpi._editedValue ?? kpi.value) : null;
      const t = calculateTotalEmissions(kpi, efEntry, rs.unitId ?? null, override);
      if (t != null) { sum += t; hasAny = true; }
    });
    return hasAny ? sum : null;
  }, [filteredList, currentRowState, isSavedTab]);

  // ── Save: Saved KPIs tab ─────────────────────────────────────────────────
  const handleSave = async () => {
    // Only save what's visible (filtered) — not all activeSavedRows
    const toSave = filteredSavedRows;
    const updatedKpis = toSave.map(kpi => {
      const rs = savedTabRowState[kpi.id] || {};
      return {
        id: kpi._savedId ?? null,
        extractedKpiId: kpi._extractedKpiId ?? kpi.id,
        answerId: kpi._answerId ?? null,
        kpiId: kpi.kpiId || null,
        kpiTitle: kpi.kpiTitle,
        value: kpi._editedValue != null ? kpi._editedValue : kpi.value,
        unitId: rs.unitId ?? null,
        fromDate: rs.periodStart || null,
        toDate: rs.periodEnd || null,
      };
    });
    const deletedKpis = deletedSavedRows
      .filter(r => r._savedId != null)
      .map(r => ({ id: r._savedId, answerId: r._answerId ?? null }));
    setSaving(true);
    try { await saveKpis(documentId, updatedKpis, deletedKpis); onSaved?.(); }
    catch (e) { console.error('Save failed', e); }
    finally { setSaving(false); }
  };

  // ── Save: Not Saved tab ───────────────────────────────────────────────────
  const handleSaveUnsaved = async () => {
    // Only save filtered rows, minus excluded
    const toSave = filteredExtractedKpis.filter(k => !excludedIds.has(k.id));
    const payload = toSave.map(kpi => {
      const rs = rowState[kpi.id] || {};
      return {
        id: null,
        extractedKpiId: kpi.id,
        answerId: null,
        kpiId: kpi.kpiId || null,
        kpiTitle: kpi.kpiTitle,
        value: kpi.value ?? kpi.rawValue,
        unitId: rs.unitId ?? null,
        fromDate: rs.periodStart || null,
        toDate: rs.periodEnd || null,
      };
    });
    setSaving(true);
    try { await saveKpis(documentId, payload, []); onSaved?.(); }
    catch (e) { console.error('Save unsaved failed', e); }
    finally { setSaving(false); }
  };

  const saveCount = isUnsavedTab
    ? filteredExtractedKpis.filter(k => !excludedIds.has(k.id)).length
    : 0;

  const GRID_SAVED   = gridCols(COL_SAVED);
  const GRID_NORMAL  = gridCols(COL_NORMAL);
  const GRID_UNSAVED = gridCols(COL_UNSAVED);
  const TOTAL_SAVED   = totalW(COL_SAVED);
  const TOTAL_NORMAL  = totalW(COL_NORMAL);
  const TOTAL_UNSAVED = totalW(COL_UNSAVED);

  const currentGrid   = isSavedTab ? GRID_SAVED : isUnsavedTab ? GRID_UNSAVED : GRID_NORMAL;
  const currentTotalW = isSavedTab ? TOTAL_SAVED : isUnsavedTab ? TOTAL_UNSAVED : TOTAL_NORMAL;

  const clearFilters = () => {
    setFilterText(''); setFilterScope(''); setFilterDb('');
    setFilterDateStart(''); setFilterDateEnd(''); setFilterNoPeriod(false);
  };
  const hasFilter = filterText || filterScope || filterDb || filterDateStart || filterDateEnd || filterNoPeriod;

  return (
    <div style={{ background: 'white', border: '1.5px solid #e2eaf0', borderRadius: 12, overflow: 'hidden', marginBottom: 20, minWidth: 0, maxWidth: '100%' }}>

      {/* ── Tab bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1.5px solid #e2eaf0', padding: '0 16px 0 4px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', overflowX: 'auto' }}>
          {TABS.map(tab => <TabBtn key={tab.key} tab={tab} active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)} />)}
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '8px 0' }}>
          {isSavedTab && (
            <>
              <GhostBtn onClick={resetSavedTab} disabled={saving}><i className="fa fa-refresh" /> Reset</GhostBtn>
              {canDelete && filteredSavedRows.length > 0 && (
                <GhostBtn onClick={deleteAllVisible} disabled={saving}>
                  <i className="fa fa-trash" style={{ color: '#e74c3c' }} />
                  <span style={{ color: '#e74c3c' }}>Delete {filteredSavedRows.length}</span>
                </GhostBtn>
              )}
              {canSave && (
                <PrimaryBtn onClick={handleSave} disabled={saving || (filteredSavedRows.length === 0 && deletedSavedRows.length === 0)}>
                  {saving ? <><Spinner as="span" animation="border" size="sm" className="me-1" />Saving…</> : <><i className="fa fa-check me-1" />Save KPIs</>}
                </PrimaryBtn>
              )}
            </>
          )}
          {canSave && isUnsavedTab && saveCount > 0 && (
            <PrimaryBtn onClick={handleSaveUnsaved} disabled={saving}>
              {saving ? <><Spinner as="span" animation="border" size="sm" className="me-1" />Saving…</> : <><i className="fa fa-check me-1" />Save {saveCount} KPI{saveCount !== 1 ? 's' : ''}</>}
            </PrimaryBtn>
          )}
          {onCancelReview && (
            <GhostBtn onClick={onCancelReview}>
              <i className={`fa ${readOnly ? 'fa-arrow-left' : 'fa-times'}`} /> {readOnly ? 'Back' : 'Close'}
            </GhostBtn>
          )}
        </div>
      </div>

      {/* ── Filter bar ── */}
      {!isLoadingTab && baseCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 16px', background: '#f7fafc', borderBottom: '1.5px solid #e2eaf0' }}>
          <i className="fa fa-filter" style={{ color: '#9ab4bf', fontSize: '0.75rem' }} />
          {/* Text search */}
          <div style={{ position: 'relative', flex: '1', minWidth: 150, maxWidth: 220 }}>
            <i className="fa fa-search" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#b0c4ce', fontSize: '0.72rem' }} />
            <input value={filterText} onChange={e => setFilterText(e.target.value)} placeholder="Search KPI title…" style={{ ...filterInputStyle, paddingLeft: 26, width: '100%' }} />
          </div>
          {allScopes.length > 0 && (
            <select value={filterScope} onChange={e => setFilterScope(e.target.value)} style={filterInputStyle}>
              <option value="">All Scopes</option>
              {allScopes.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {allDbs.length > 0 && (
            <select value={filterDb} onChange={e => setFilterDb(e.target.value)} style={filterInputStyle}>
              <option value="">All Databases</option>
              {allDbs.map(db => <option key={db} value={db}>{db}</option>)}
            </select>
          )}
          {/* Period filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9ab4bf', whiteSpace: 'nowrap' }}>Period</span>
            <input type="date" value={filterDateStart} onChange={e => { setFilterDateStart(e.target.value); setFilterNoPeriod(false); }} style={{ ...filterInputStyle, width: 130 }} />
            <span style={{ fontSize: '0.72rem', color: '#b0c4ce' }}>–</span>
            <input type="date" value={filterDateEnd} onChange={e => { setFilterDateEnd(e.target.value); setFilterNoPeriod(false); }} style={{ ...filterInputStyle, width: 130 }} />
          </div>
          {/* "No period set" quick filter — shown in unsaved tab only */}
          {isUnsavedTab && (
            <button
              onClick={() => { setFilterNoPeriod(p => !p); setFilterDateStart(''); setFilterDateEnd(''); }}
              style={{ ...filterInputStyle, cursor: 'pointer', background: filterNoPeriod ? 'rgba(231,76,60,0.08)' : 'white', borderColor: filterNoPeriod ? '#e74c3c' : '#e2eaf0', color: filterNoPeriod ? '#e74c3c' : '#6b8794', fontWeight: filterNoPeriod ? 700 : 400, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}
              title="Show only KPIs with no period set"
            >
              <i className="fa fa-calendar-times-o" style={{ fontSize: '0.75rem' }} />
              No date set
            </button>
          )}
          {hasFilter && (
            <>
              <button onClick={clearFilters} style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: '0.8rem', cursor: 'pointer', padding: '0 4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                <i className="fa fa-times" /> Clear
              </button>
              <span style={{ fontSize: '0.78rem', color: '#6b8794' }}>{filteredList.length} of {baseCount} shown</span>
            </>
          )}
        </div>
      )}

      {/* ── Table ── */}
      {isLoadingTab ? (
        <LoadingState label="Loading KPIs…" />
      ) : (
        <>
          {/* Empty state: only when no rows and no deleted rows */}
          {filteredList.length === 0 && !(isSavedTab && deletedSavedRows.length > 0) && (
            <EmptyState icon="🔍" message={baseCount === 0 ? 'No KPIs in this category.' : 'No KPIs match the current filter.'} />
          )}

          {/* Main table */}
          {filteredList.length > 0 && (
            <div style={{ overflowX: 'auto', overflowY: 'visible', width: '100%' }}>
              <div style={{ minWidth: currentTotalW }}>
                <HeaderRow
                  allDbs={allDbs}
                  onApplyAll={(patch) => filteredList.forEach(kpi => currentUpdateRow(kpi.id, patch))}
                  isSavedTab={isSavedTab}
                  isUnsavedTab={isUnsavedTab}
                  grid={currentGrid}
                  totalEmissions={totalEmissions}
                  deletedCount={isSavedTab ? deletedSavedRows.length : 0}
                  filteredList={filteredList}
                  excludedIds={excludedIds}
                  onSelectAll={() => selectAllVisible(filteredList)}
                  onDeselectAll={() => deselectAllVisible(filteredList)}
                  readOnly={readOnly}
                />
                {filteredList.map((kpi, idx) => {
                  const isLast = idx === filteredList.length - 1;
                  if (isSavedTab) {
                    return (
                      <SavedKpiRow
                        key={kpi.id} kpi={kpi} rs={savedTabRowState[kpi.id] || {}}
                        onUpdate={(patch) => updateSavedTabRow(kpi.id, patch)}
                        onValueChange={(v) => updateSavedRowValue(kpi.id, v)}
                        onDelete={() => deleteSavedRow(kpi.id)}
                        isEven={idx % 2 === 0} isLast={isLast} grid={GRID_SAVED}
                      />
                    );
                  }
                  return (
                    <KpiRow
                      key={kpi.id} kpi={kpi} rs={rowState[kpi.id] || {}}
                      onUpdate={(patch) => updateRow(kpi.id, patch)}
                      isEven={idx % 2 === 0} isLast={isLast} grid={currentGrid}
                      isUnsavedTab={isUnsavedTab}
                      readOnly={readOnly}
                      isExcluded={excludedIds.has(kpi.id)}
                      onToggleExclude={() => toggleExclude(kpi.id)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Marked for Deletion section — always visible, never filtered ── */}
          {isSavedTab && deletedSavedRows.length > 0 && (
            <div style={{ borderTop: '2px dashed rgba(231,76,60,0.25)', background: 'rgba(231,76,60,0.02)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', borderBottom: '1px solid rgba(231,76,60,0.12)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <i className="fa fa-trash" style={{ color: '#e74c3c', fontSize: '0.8rem' }} />
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#e74c3c' }}>Marked for Deletion</span>
                  <span style={{ background: 'rgba(231,76,60,0.12)', color: '#e74c3c', borderRadius: 10, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 600 }}>
                    {deletedSavedRows.length}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#9ab4bf' }}>These will be permanently deleted on save</span>
                </div>
                <button onClick={restoreAllDeleted}
                  style={{ background: 'none', border: '1px solid #3f88a5', color: '#3f88a5', borderRadius: 5, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                  <i className="fa fa-undo me-1" />Restore All
                </button>
              </div>
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <div style={{ minWidth: TOTAL_SAVED }}>
                  {deletedSavedRows.map((kpi, idx) => (
                    <DeletedRow key={kpi.id} kpi={kpi} onRestore={() => restoreSavedRow(kpi.id)}
                      isLast={idx === deletedSavedRows.length - 1} grid={GRID_SAVED} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Header row ───────────────────────────────────────────────────────────────

const HeaderRow = ({ allDbs, onApplyAll, isSavedTab, isUnsavedTab, grid, totalEmissions, deletedCount, filteredList, excludedIds, onSelectAll, onDeselectAll, readOnly }) => {
  // Compute tristate: all selected, none selected, or mixed
  const allSelected   = filteredList.length > 0 && filteredList.every(k => !excludedIds.has(k.id));
  const noneSelected  = filteredList.length > 0 && filteredList.every(k => excludedIds.has(k.id));
  const handleToggleAll = () => allSelected ? onDeselectAll() : onSelectAll();

  return (
  <div style={{ display: 'grid', gridTemplateColumns: grid, padding: '0 16px', background: '#f7fafc', borderBottom: '1.5px solid #e2eaf0' }}>
    {/* Select-all checkbox in Not Saved tab */}
    {isUnsavedTab && (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '9px 0' }}>
        <input
          type="checkbox"
          checked={allSelected}
          ref={el => { if (el) el.indeterminate = !allSelected && !noneSelected; }}
          onChange={handleToggleAll}
          title={allSelected ? 'Deselect all' : 'Select all'}
          style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#3f88a5' }}
        />
      </div>
    )}
    <ColHeader label="Raw Title" />
    <ColHeader label="KPI Title" />
    <ColHeader label="Raw Value" />
    <ColHeader label="Value" />
    <ColHeader label="Unit" />
    {readOnly ? <ColHeader label="Period" /> : (
      <ApplyAllHeader label="Period">
        {({ close }) => <PeriodApplyPanel onApply={(s, e) => { onApplyAll({ periodStart: s, periodEnd: e }); close(); }} />}
      </ApplyAllHeader>
    )}
    <ColHeader label="Scope" tooltip="ESG scope category as classified by the AI (e.g. Scope 1, Scope 2, Scope 3 / Purchased Goods and Services)" />
    {readOnly ? <ColHeader label="GHG Database" /> : (
      <ApplyAllHeader label="GHG Database">
        {({ close }) => <DbApplyPanel allDbs={allDbs} onApply={(db) => { onApplyAll({ db }); close(); }} />}
      </ApplyAllHeader>
    )}
    <ColHeader label="Emission Factor" />
    {/* Total Emissions — shows sum below label */}
    <div style={{ padding: '7px 8px 7px 0' }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b8794', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
        Total Emissions
      </div>
      {totalEmissions != null && (
        <div style={{ fontSize: '0.82rem', fontWeight: 700, whiteSpace: 'nowrap', marginTop: 2 }}>
          <span style={{ color: '#1a2e38' }}>
            {totalEmissions >= 1e6
              ? `${(totalEmissions / 1e6).toFixed(3)} t`
              : `${totalEmissions.toFixed(3)} kg`}
          </span>
          <span style={{ fontWeight: 400, color: '#9ab4bf', fontSize: '0.72rem' }}> CO₂e</span>
          {deletedCount > 0 && (
            <span style={{ marginLeft: 6, color: '#e74c3c', fontSize: '0.67rem', fontWeight: 600 }}>({deletedCount} to delete)</span>
          )}
        </div>
      )}
    </div>
    {isSavedTab && <ColHeader label="" />}
  </div>
  );
};

const ColHeader = ({ label, tooltip }) => {
  const inner = (
    <div style={{ padding: '9px 8px 9px 0', fontSize: '0.68rem', fontWeight: 700, color: '#6b8794', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 4 }}>
      {label}
      {tooltip && <i className="fa fa-info-circle" style={{ fontSize: '0.65rem', color: '#b0c4ce', flexShrink: 0 }} />}
    </div>
  );
  return tooltip ? <Tooltip text={tooltip}>{inner}</Tooltip> : inner;
};

const ApplyAllHeader = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative', padding: '9px 8px 9px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b8794', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{label}</span>
        <button onClick={() => setOpen(o => !o)} title={`Apply ${label} to all visible rows`}
          style={{ background: open ? 'rgba(63,136,165,0.12)' : 'transparent', border: `1px solid ${open ? '#3f88a5' : '#d0dde4'}`, borderRadius: 4, cursor: 'pointer', padding: '1px 5px', display: 'flex', alignItems: 'center', gap: 3, color: open ? '#3f88a5' : '#9ab4bf', fontSize: '0.62rem', fontWeight: 700, transition: 'all 0.15s', lineHeight: 1.5 }}
          onMouseEnter={e => { if (!open) { e.currentTarget.style.borderColor = '#3f88a5'; e.currentTarget.style.color = '#3f88a5'; } }}
          onMouseLeave={e => { if (!open) { e.currentTarget.style.borderColor = '#d0dde4'; e.currentTarget.style.color = '#9ab4bf'; } }}
        >
          <i className="fa fa-magic" style={{ fontSize: '0.58rem' }} /> All
        </button>
      </div>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 2px)', left: 0, zIndex: 200, background: 'white', border: '1.5px solid #e2eaf0', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,0.13)', minWidth: 230, padding: 14 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#3f88a5', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Apply to all visible rows</div>
          {children({ close: () => setOpen(false) })}
        </div>
      )}
    </div>
  );
};

const PeriodApplyPanel = ({ onApply }) => {
  const [s, setS] = useState(''); const [e, setE] = useState('');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div><div style={panelLabel}>Period Start</div><input type="date" value={s} onChange={ev => setS(ev.target.value)} style={{ ...applyInputStyle, width: '100%' }} /></div>
      <div><div style={panelLabel}>Period End</div><input type="date" value={e} onChange={ev => setE(ev.target.value)} style={{ ...applyInputStyle, width: '100%' }} /></div>
      <button onClick={() => { if (s || e) onApply(s, e); }} disabled={!s && !e}
        style={{ marginTop: 4, background: '#3f88a5', border: 'none', color: 'white', borderRadius: 6, padding: '7px 0', fontSize: '0.82rem', fontWeight: 600, cursor: (!s && !e) ? 'not-allowed' : 'pointer', opacity: (!s && !e) ? 0.5 : 1, width: '100%' }}>
        Apply to All
      </button>
    </div>
  );
};

const DbApplyPanel = ({ allDbs, onApply }) => {
  const [db, setDb] = useState('');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div><div style={panelLabel}>GHG Database</div>
        <select value={db} onChange={e => setDb(e.target.value)} style={{ ...applyInputStyle, width: '100%' }}>
          <option value="">Select database…</option>
          {allDbs.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      <button onClick={() => { if (db) onApply(db); }} disabled={!db}
        style={{ marginTop: 4, background: '#3f88a5', border: 'none', color: 'white', borderRadius: 6, padding: '7px 0', fontSize: '0.82rem', fontWeight: 600, cursor: !db ? 'not-allowed' : 'pointer', opacity: !db ? 0.5 : 1, width: '100%' }}>
        Apply to All
      </button>
    </div>
  );
};

// ─── KPI row (extracted tabs) ─────────────────────────────────────────────────

const KpiRow = ({ kpi, rs, onUpdate, isEven, isLast, grid, isUnsavedTab, readOnly, isExcluded, onToggleExclude }) => {
  const [canSavePermission] = usePermission("doc-sense.save-kpis", "LIST_DOCUMENTS");
  const canSave = canSavePermission && !readOnly;

  const unitMap = buildUnitMap(kpi);
  const uniqueDbs = getUniqueDbs(kpi);
  const selectedUnitId = rs.unitId ?? null;
  const selectedDb = rs.db ?? '';
  const periodStart = rs.periodStart ?? '';
  const periodEnd = rs.periodEnd ?? '';
  const efEntry = findEfEntry(kpi, selectedDb, selectedUnitId);
  const efUnitFactor = kpi.supportedUnits?.find(u => u.unitId === efEntry?.unitId)?.factor ?? 1;
  const selectedUnitFactor = kpi.supportedUnits?.find(u => u.unitId === selectedUnitId)?.factor ?? 1;
  const scaledEF = efEntry != null ? parseFloat(efEntry.emissionFactor) * selectedUnitFactor / efUnitFactor : null;
  const efDisplayUnit = kpiUnit(kpi, selectedUnitId) || efEntry?.unit || '';
  const displayValue = kpi.value ?? kpi.rawValue;
  const total = calculateTotalEmissions(kpi, efEntry, selectedUnitId, null);
  const rawValue = kpi.rawResponse?.rawResponse?.original?.value ?? kpi.rawValue ?? kpi.value;
  const unitLabel = (uid) => unitMap[uid] || `Unit ${uid}`;
  const periodDisplay = (periodStart || periodEnd) ? `${fmtDate(periodStart)} – ${fmtDate(periodEnd)}` : '—';
  const noPeriod = !periodStart && !periodEnd;
  const cell = { fontSize: '0.82rem', paddingRight: 8, overflow: 'hidden' };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: grid, padding: '10px 16px', alignItems: 'center', borderBottom: isLast ? 'none' : '1px solid rgba(0,0,0,0.05)', background: isExcluded ? 'rgba(0,0,0,0.025)' : isEven ? 'white' : '#fafcfd', minHeight: 52, opacity: isExcluded ? 0.5 : 1 }}>

      {/* Exclude checkbox — only in Not Saved tab */}
      {isUnsavedTab && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <input type="checkbox" checked={!isExcluded} onChange={onToggleExclude}
            disabled={!canSave}
            title={isExcluded ? 'Include in save' : 'Exclude from save'}
            style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#3f88a5' }} />
        </div>
      )}

      <Tooltip text={kpi.rawTitle}><Td style={{ ...cell, color: '#6b8794' }}>{kpi.rawTitle || '—'}</Td></Tooltip>
      <Tooltip text={kpi.kpiTitle}><Td style={{ ...cell, fontWeight: 600, color: '#1a2e38' }}>{kpi.kpiTitle || '—'}</Td></Tooltip>
      <Td style={{ ...cell, color: '#6b8794' }}>{rawValue != null ? `${fmtVal(rawValue)}${kpi.rawUnit ? ` ${kpi.rawUnit}` : ''}` : '—'}</Td>
      <Td style={{ ...cell, fontWeight: 600, color: displayValue != null ? '#1a2e38' : '#b0c4ce' }}>{fmtVal(displayValue)}</Td>

      {/* Unit */}
      <div style={{ paddingRight: 8 }}>
        {kpi.supportedUnits?.length > 0 ? (
          <select value={selectedUnitId ?? ''} onChange={e => onUpdate({ unitId: Number(e.target.value) })} disabled={!canSave} style={selStyle}>
            {kpi.supportedUnits.map(u => <option key={u.unitId} value={u.unitId}>{unitLabel(u.unitId)}</option>)}
          </select>
        ) : <span style={{ fontSize: '0.82rem', color: '#6b8794' }}>{kpi.unit || kpi.rawUnit || '—'}</span>}
      </div>

      {/* Period — highlight if no date (in unsaved tab) */}
      <div style={{ paddingRight: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <input type="date" value={periodStart} onChange={e => onUpdate({ periodStart: e.target.value })}
            disabled={!canSave}
            style={{ ...selStyle, padding: '3px 5px', borderColor: (isUnsavedTab && noPeriod) ? '#f0a500' : '#e2eaf0' }} />
          <input type="date" value={periodEnd} onChange={e => onUpdate({ periodEnd: e.target.value })}
            disabled={!canSave}
            style={{ ...selStyle, padding: '3px 5px', borderColor: (isUnsavedTab && noPeriod) ? '#f0a500' : '#e2eaf0' }} />
        </div>
      </div>

      <Tooltip text={fmtScope(kpi) || 'No scope assigned'}>
        <Td style={{ ...cell, color: fmtScope(kpi) ? '#6b8794' : '#b0c4ce', fontSize: '0.8rem' }}>{fmtScope(kpi) || '—'}</Td>
      </Tooltip>

      {/* GHG DB */}
      <div style={{ paddingRight: 8 }}>
        {uniqueDbs.length > 0 ? (
          <select value={selectedDb} onChange={e => onUpdate({ db: e.target.value })} disabled={!canSave} style={{ ...selStyle, width: '100%' }}>
            {uniqueDbs.map(db => <option key={db} value={db}>{db}</option>)}
          </select>
        ) : <span style={{ fontSize: '0.78rem', color: '#b0c4ce' }}>—</span>}
      </div>

      {/* EF */}
      <div style={{ paddingRight: 8 }}>
        {efEntry ? (
          <div>
            <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#1a2e38', whiteSpace: 'nowrap' }}>{scaledEF != null ? scaledEF.toFixed(4) : '—'}</div>
            <div style={{ fontSize: '0.7rem', color: '#9ab4bf', whiteSpace: 'nowrap' }}>kg CO₂e / {efDisplayUnit}</div>
          </div>
        ) : <span style={{ fontSize: '0.82rem', color: '#b0c4ce' }}>—</span>}
      </div>

      {/* Total */}
      <div>
        {total != null ? (
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#1a2e38' }}>{total.toFixed(3)}</div>
            <div style={{ fontSize: '0.7rem', color: '#9ab4bf' }}>kg CO₂e</div>
          </div>
        ) : <span style={{ fontSize: '0.82rem', color: '#b0c4ce' }}>—</span>}
      </div>
    </div>
  );
};

// ─── Saved KPI row (editable) ─────────────────────────────────────────────────

const SavedKpiRow = ({ kpi, rs, onUpdate, onValueChange, onDelete, isEven, isLast, grid }) => {
  const [canSave] = usePermission("doc-sense.save-kpis", "LIST_DOCUMENTS");
  const [canDelete] = usePermission("doc-sense.delete-kpi", "LIST_DOCUMENTS");

  const unitMap = buildUnitMap(kpi);
  const uniqueDbs = getUniqueDbs(kpi);
  const selectedUnitId = rs.unitId ?? null;
  const selectedDb = rs.db ?? '';
  const periodStart = rs.periodStart ?? '';
  const periodEnd = rs.periodEnd ?? '';
  const efEntry = findEfEntry(kpi, selectedDb, selectedUnitId);
  const efUnitFactor = kpi.supportedUnits?.find(u => u.unitId === efEntry?.unitId)?.factor ?? 1;
  const selectedUnitFactor = kpi.supportedUnits?.find(u => u.unitId === selectedUnitId)?.factor ?? 1;
  const scaledEF = efEntry != null ? parseFloat(efEntry.emissionFactor) * selectedUnitFactor / efUnitFactor : null;
  const efDisplayUnit = kpiUnit(kpi, selectedUnitId) || efEntry?.unit || '';
  const displayVal = kpi._editedValue != null ? kpi._editedValue : kpi.value;
  const total = calculateTotalEmissions(kpi, efEntry, selectedUnitId, displayVal);
  const rawValue = kpi.rawResponse?.rawResponse?.original?.value ?? kpi.rawValue ?? kpi.value;
  const unitLabel = (uid) => unitMap[uid] || `Unit ${uid}`;
  const cell = { fontSize: '0.82rem', paddingRight: 8, overflow: 'hidden' };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: grid, padding: '10px 16px', alignItems: 'center', borderBottom: isLast ? 'none' : '1px solid rgba(0,0,0,0.05)', background: isEven ? 'white' : '#fafcfd', minHeight: 52 }}>
      <Tooltip text={kpi.rawTitle}><Td style={{ ...cell, color: '#6b8794' }}>{kpi.rawTitle || '—'}</Td></Tooltip>
      <Tooltip text={kpi.kpiTitle}><Td style={{ ...cell, fontWeight: 600, color: '#1a2e38' }}>{kpi.kpiTitle || '—'}</Td></Tooltip>
      <Td style={{ ...cell, color: '#6b8794' }}>{rawValue != null ? `${fmtVal(rawValue)}${kpi.rawUnit ? ` ${kpi.rawUnit}` : ''}` : '—'}</Td>

      {/* Editable value */}
      <div style={{ paddingRight: 8 }}>
        <input type="number" value={displayVal ?? ''} onChange={e => onValueChange(e.target.value === '' ? null : e.target.value)}
          disabled={!canSave}
          style={{ ...selStyle, padding: '3px 5px', fontWeight: 600 }} />
      </div>

      {/* Unit */}
      <div style={{ paddingRight: 8 }}>
        {kpi.supportedUnits?.length > 0 ? (
          <select value={selectedUnitId ?? ''} onChange={e => onUpdate({ unitId: Number(e.target.value) })} disabled={!canSave} style={selStyle}>
            {kpi.supportedUnits.map(u => <option key={u.unitId} value={u.unitId}>{unitLabel(u.unitId)}</option>)}
          </select>
        ) : <span style={{ fontSize: '0.82rem', color: '#6b8794' }}>{kpi.unit || '—'}</span>}
      </div>

      {/* Period */}
      <div style={{ paddingRight: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <input type="date" value={periodStart} onChange={e => onUpdate({ periodStart: e.target.value })} disabled={!canSave} style={{ ...selStyle, padding: '3px 5px' }} />
          <input type="date" value={periodEnd}   onChange={e => onUpdate({ periodEnd:   e.target.value })} disabled={!canSave} style={{ ...selStyle, padding: '3px 5px' }} />
        </div>
      </div>

      <Tooltip text={fmtScope(kpi) || 'No scope assigned'}>
        <div style={{ fontSize: '0.8rem', color: fmtScope(kpi) ? '#6b8794' : '#b0c4ce', paddingRight: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fmtScope(kpi) || '—'}</div>
      </Tooltip>

      {/* GHG DB */}
      <div style={{ paddingRight: 8 }}>
        {uniqueDbs.length > 0 ? (
          <select value={selectedDb} onChange={e => onUpdate({ db: e.target.value })} disabled={!canSave} style={{ ...selStyle, width: '100%' }}>
            {uniqueDbs.map(db => <option key={db} value={db}>{db}</option>)}
          </select>
        ) : <span style={{ fontSize: '0.78rem', color: '#b0c4ce' }}>—</span>}
      </div>

      {/* EF */}
      <div style={{ paddingRight: 8 }}>
        {efEntry ? (
          <div>
            <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#1a2e38', whiteSpace: 'nowrap' }}>{scaledEF != null ? scaledEF.toFixed(4) : '—'}</div>
            <div style={{ fontSize: '0.7rem', color: '#9ab4bf', whiteSpace: 'nowrap' }}>kg CO₂e / {efDisplayUnit}</div>
          </div>
        ) : <span style={{ fontSize: '0.82rem', color: '#b0c4ce' }}>—</span>}
      </div>

      {/* Total */}
      <div>
        {total != null ? (
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#1a2e38' }}>{total.toFixed(3)}</div>
            <div style={{ fontSize: '0.7rem', color: '#9ab4bf' }}>kg CO₂e</div>
          </div>
        ) : <span style={{ fontSize: '0.82rem', color: '#b0c4ce' }}>—</span>}
      </div>

      {/* Delete */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {canDelete && (
          <button onClick={onDelete} title="Remove this KPI"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b0c4ce', padding: 4, borderRadius: 4, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = '#e74c3c'}
            onMouseLeave={e => e.currentTarget.style.color = '#b0c4ce'}>
            <i className="fa fa-trash" style={{ fontSize: '0.85rem' }} />
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Deleted row ──────────────────────────────────────────────────────────────

const DeletedRow = ({ kpi, onRestore, isLast, grid }) => (
  <div style={{ display: 'grid', gridTemplateColumns: grid, padding: '8px 16px', alignItems: 'center', borderBottom: isLast ? 'none' : '1px solid rgba(0,0,0,0.04)', background: 'rgba(231,76,60,0.03)', opacity: 0.55 }}>
    <Tooltip text={kpi.rawTitle}><Td style={{ fontSize: '0.82rem', color: '#6b8794', textDecoration: 'line-through', paddingRight: 8, overflow: 'hidden' }}>{kpi.rawTitle || '—'}</Td></Tooltip>
    <Tooltip text={kpi.kpiTitle}><Td style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a2e38', textDecoration: 'line-through', paddingRight: 8, overflow: 'hidden' }}>{kpi.kpiTitle || '—'}</Td></Tooltip>
    <div style={{ gridColumn: 'span 8', fontSize: '0.78rem', color: '#e74c3c', paddingLeft: 8 }}>Marked for deletion</div>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <button onClick={onRestore} style={{ background: 'none', border: '1px solid #3f88a5', cursor: 'pointer', color: '#3f88a5', padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 }}>Restore</button>
    </div>
  </div>
);

// ─── Shared ───────────────────────────────────────────────────────────────────

const Td = ({ children, style }) => (
  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...style }}>{children}</div>
);

const TabBtn = ({ tab, active, onClick }) => (
  <button onClick={onClick} style={{ background: 'none', border: 'none', borderBottom: active ? '2.5px solid #3f88a5' : '2.5px solid transparent', padding: '13px 16px 11px', fontSize: '0.855rem', fontWeight: active ? 700 : 500, color: active ? '#3f88a5' : '#6b8794', cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
    {tab.label}
    <span style={{ background: tab.accent ? 'rgba(231,76,60,0.12)' : active ? 'rgba(63,136,165,0.12)' : 'rgba(0,0,0,0.06)', color: tab.accent ? '#e74c3c' : active ? '#3f88a5' : '#888', borderRadius: 10, padding: '1px 7px', fontSize: '0.7rem', fontWeight: 600 }}>{tab.count}</span>
  </button>
);

const GhostBtn = ({ children, onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ background: 'transparent', border: '1.5px solid #e2eaf0', color: '#6b8794', borderRadius: 7, padding: '6px 12px', fontSize: '0.82rem', fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: disabled ? 0.6 : 1 }}
    onMouseEnter={e => { if (!disabled) { e.currentTarget.style.borderColor = '#3f88a5'; e.currentTarget.style.color = '#3f88a5'; } }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2eaf0'; e.currentTarget.style.color = '#6b8794'; }}>
    {children}
  </button>
);

const PrimaryBtn = ({ children, onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ background: disabled ? '#9ab4bf' : '#3f88a5', border: 'none', color: 'white', borderRadius: 7, padding: '6px 16px', fontSize: '0.82rem', fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'background 0.15s' }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = '#2d6a84'; }}
    onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = '#3f88a5'; }}>
    {children}
  </button>
);

const LoadingState = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: 12, color: '#6b8794' }}>
    <Spinner animation="border" size="sm" style={{ color: '#3f88a5' }} />
    <span style={{ fontSize: '0.875rem' }}>{label}</span>
  </div>
);

const EmptyState = ({ icon, message }) => (
  <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b8794' }}>
    <div style={{ fontSize: '2.5rem', opacity: 0.3, marginBottom: 12 }}>{icon}</div>
    <p style={{ margin: 0, fontSize: '0.875rem' }}>{message}</p>
  </div>
);

const selStyle = { border: '1.5px solid #e2eaf0', borderRadius: 6, padding: '3px 6px', fontSize: '0.78rem', color: '#1a2e38', outline: 'none', background: 'white', cursor: 'pointer', width: '100%' };
const filterInputStyle = { border: '1.5px solid #e2eaf0', borderRadius: 6, padding: '5px 9px', fontSize: '0.82rem', color: '#1a2e38', outline: 'none', background: 'white' };
const applyInputStyle  = { border: '1.5px solid #e2eaf0', borderRadius: 6, padding: '5px 8px', fontSize: '0.82rem', color: '#1a2e38', outline: 'none', background: 'white' };
const panelLabel = { fontSize: '0.72rem', fontWeight: 600, color: '#6b8794', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 };

export default KpiReviewTable;